import type { ScenarioInputs, ScenarioResult } from './financialCalculations';
import { computeScenario } from './financialCalculations';

/**
 * APPLYING A PROPOSED SCENARIO TO THE ENGINE.
 *
 * ⭐⭐ THIS IS THE HALF THAT MAKES THE FEATURE HONEST. `scenarioBuilderService` lets a model pick
 * LEVERS from a closed set; this turns those levers into `ScenarioInputs` and hands them to
 * `computeScenario`. **The model never produces a figure - the engine produces all of them**,
 * from the same base the sensitivity tables use, so a scenario cannot disagree with the document
 * around it.
 *
 * ⛔ ONE IMPLEMENTATION, TWO CONSUMERS - the analyzer screen and `pdfExport`. The dominant defect
 * class in this build is one part of the app modelling a deal differently from another, and a
 * scenario that reads one way on screen and another in the client's PDF is that defect on a page
 * Joseph signs.
 */
export interface ScenarioLeverInput {
  label: string;
  lever: string;
  value: number;
  year?: number;
}

/** Levers the engine can run. Kept in step with `SCENARIO_LEVERS` in the backend service. */
export const KNOWN_LEVERS = [
  'vacancy_pts', 'exit_cap_bps', 'income_growth_pts', 'retenanting_cost', 'noi_year',
] as const;

/**
 * Fold the levers into a copy of the base inputs.
 *
 * ⚠️ NON-DESTRUCTIVE. `noiOverrides` and `capitalCostsByYear` are COPIED before being written,
 * because the base object is the one the summary path and the sensitivity tables share - mutating
 * it here would silently re-price the whole document from a scenario the reader never applied.
 *
 * ⚠️ An unknown lever is IGNORED here rather than guessed at. The backend already refuses one, so
 * reaching this with an unknown lever means the two lists have drifted - and quietly modelling it
 * as something else would be worse than modelling nothing.
 */
export const applyScenarioLevers = (
  base: ScenarioInputs,
  levers: ScenarioLeverInput[]
): { inputs: ScenarioInputs; applied: ScenarioLeverInput[]; ignored: ScenarioLeverInput[] } => {
  const next: ScenarioInputs = {
    ...base,
    noiOverrides: base.noiOverrides ? [...base.noiOverrides] : undefined,
    capitalCostsByYear: base.capitalCostsByYear ? [...base.capitalCostsByYear] : undefined,
  };
  const applied: ScenarioLeverInput[] = [];
  const ignored: ScenarioLeverInput[] = [];

  for (const l of levers) {
    switch (l.lever) {
      case 'vacancy_pts':
        next.vacancyRate = Math.min(100, Math.max(0, next.vacancyRate + l.value));
        applied.push(l); break;
      case 'exit_cap_bps':
        // bps, so 50 is half a point. Floored just above zero - a zero exit cap is an infinite
        // sale price, which would print a triumphant IRR off a nonsense number.
        next.exitCapRate = Math.max(0.01, next.exitCapRate + l.value / 100);
        applied.push(l); break;
      case 'income_growth_pts':
        // REMOVED from growth, which is what the backend prompt tells the model it means.
        next.incomeGrowthRate = next.incomeGrowthRate - l.value;
        applied.push(l); break;
      case 'retenanting_cost': {
        if (!l.year || l.year < 1) { ignored.push(l); break; }
        const arr = next.capitalCostsByYear ? [...next.capitalCostsByYear] : [];
        while (arr.length < l.year) arr.push(0);
        arr[l.year - 1] = (arr[l.year - 1] ?? 0) + l.value;
        next.capitalCostsByYear = arr;
        applied.push(l); break;
      }
      case 'noi_year': {
        if (!l.year || l.year < 1) { ignored.push(l); break; }
        const arr = next.noiOverrides ? [...next.noiOverrides] : [];
        while (arr.length < l.year) arr.push(null);
        arr[l.year - 1] = l.value;
        next.noiOverrides = arr;
        applied.push(l); break;
      }
      default:
        ignored.push(l);
    }
  }
  return { inputs: next, applied, ignored };
};

export interface ScenarioOutcome {
  base: ScenarioResult;
  modelled: ScenarioResult;
  applied: ScenarioLeverInput[];
  ignored: ScenarioLeverInput[];
}

/** Base and modelled, side by side, both from the engine. */
export const runScenario = (base: ScenarioInputs, levers: ScenarioLeverInput[]): ScenarioOutcome => {
  const { inputs, applied, ignored } = applyScenarioLevers(base, levers);
  // 🚨 `0, 0`, NOT `1, 1`. priceMod and pgiMod are PERCENTAGES applied as
  // `purchasePrice * (1 + priceMod / 100)`, so zero means "no modifier". Passing 1 priced this
  // base 1% above every other consumer and printed a base IRR of -1.15% against the screen's
  // 6.92% on the same deal. `buildScenarioSet` and the sensitivity grid both pass `0, 0`.
  // ⭐⭐ THE UNIT TESTS COULD NOT SEE IT: they compared modelled against base, and BOTH were
  // wrong by the same amount. A relative assertion cannot catch an absolute error - found by
  // opening the app and reading a number that should have been familiar.
  return {
    base: computeScenario(base, 0, 0),
    modelled: computeScenario(inputs, 0, 0),
    applied,
    ignored,
  };
};
